import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import SectionDefinitionCard from './SectionDefinitionCard';

const SectionDefinitionGrid = ({
  definitions = [],
  selectedId,
  onEdit,
  onDelete,
  onReorder,
}) => {
  const [items, setItems] = useState(definitions);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  useEffect(() => {
    setItems(definitions);
  }, [definitions]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => String(item._id) === String(active.id));
    const newIndex = items.findIndex((item) => String(item._id) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    const moving = items[oldIndex];
    const target = items[newIndex];
    if (moving?.role === 'additional_knowledge' || target?.role === 'additional_knowledge') return;

    const previous = items;
    const next = arrayMove(items, oldIndex, newIndex);
    const additional = next.find((item) => item.role === 'additional_knowledge');
    const ordered = additional
      ? [...next.filter((item) => item.role !== 'additional_knowledge'), additional]
      : next;
    setItems(ordered);
    try {
      await onReorder(ordered.map((item) => item._id));
    } catch {
      setItems(previous);
    }
  };

  if (items.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        No cards yet. Add a card to start this template.
      </Typography>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((item) => String(item._id))} strategy={rectSortingStrategy}>
        <Box
          display='grid'
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }}
          gap={1.5}
        >
          {(() => {
            const additional = items.find((item) => item.role === 'additional_knowledge');
            const standard = items.filter((item) => item.role !== 'additional_knowledge');
            const spacers = additional ? (5 - (standard.length % 3)) % 3 : 0;
            const cells = [
              ...standard,
              ...Array.from({ length: spacers }, (_, index) => ({ _id: `spacer-${index}`, spacer: true })),
              ...(additional ? [additional] : []),
            ];
            return cells.map((definition) =>
              definition.spacer ? (
                <Box key={definition._id} sx={{ display: { xs: 'none', md: 'block' } }} />
              ) : (
                <SectionDefinitionCard
                  key={definition._id}
                  definition={definition}
                  selected={String(selectedId) === String(definition._id)}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ),
            );
          })()}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default SectionDefinitionGrid;
